import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getPriceAlertsWatchlistAPI,
  addSingleAsinAPI,
  deleteSingleAsinAPI,
  deleteAsinsBatchAPI,
  exportAsinsAPI,
  getPriceAlertInfoAPI,
} from "../../lib/api/api";

export const fetchAsinWatchlist = createAsyncThunk(
  "asinWatchlist/fetchAsinWatchlist",
  async (_, { dispatch }) => {
    const response = await getPriceAlertsWatchlistAPI();
    const status = response?.status;

    if (status === "unauthorized") {
      dispatch(setAuthStatus(status));
    }
    return response;
  }
);

export const addSingleAsin = createAsyncThunk(
  "asinWatchlist/addSingleAsin",
  async (asin, { dispatch }) => {
    const response = await addSingleAsinAPI(asin);
    const status = response?.status;

    if (status === "unauthorized") {
      dispatch(setAuthStatus(status));
    }
    return response;
  }
);

export const deleteSingleAsin = createAsyncThunk(
  "asinWatchlist/deleteSingleAsin",
  async (asin, { dispatch }) => {
    const response = await deleteSingleAsinAPI(asin);
    const status = response?.status;

    if (status === "unauthorized") {
      dispatch(setAuthStatus(status));
    }
    return response;
  }
);

export const deleteAsinsBatch = createAsyncThunk(
  "asinWatchlist/deleteAsinsBatch",
  async (asins, { dispatch }) => {
    const response = await deleteAsinsBatchAPI(asins);
    const status = response?.status;

    if (status === "unauthorized") {
      dispatch(setAuthStatus(status));
    }
    return response;
  }
);

export const exportAsins = createAsyncThunk(
  "asinWatchlist/exportAsins",
  async (_, { dispatch }) => {
    const response = await exportAsinsAPI();
    const status = response?.status;

    if (status === "unauthorized") {
      dispatch(setAuthStatus(status));
      return { status };
    }

    if (response instanceof Blob) {
      const url = window.URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "asins-export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }

    return { status: "authorized" };
  }
);

export const fetchPriceAlertInfo = createAsyncThunk(
  "asinWatchlist/fetchPriceAlertInfo",
  async (_, { dispatch }) => {
    const response = await getPriceAlertInfoAPI();
    const status = response?.status;

    if (status === "unauthorized") {
      dispatch(setAuthStatus(status));
    }
    return response;
  }
);

const initialFilters = {
  search: "",
  title: "",
  brand: "",
  category: "",
  asin: "",
  bsrMin: "",
  bsrMax: "",
};

const asinWatchlistSlice = createSlice({
  name: "asinWatchlist",
  initialState: {
    watchlist: [],
    status: "idle",
    error: null,
    currentPage: 0,
    totalPages: 1,
    totalItems: 0,
    authStatus: null,
    asinTokens: 0,
    asinsLeft: 0,
    asinsLocked: 0,
    trackedAsins: 0,
    filters: { ...initialFilters },
    selected: [],
  },
  reducers: {
    setAuthStatus: (state, action) => {
      state.authStatus = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    resetFilters: (state) => {
      state.filters = { ...initialFilters };
    },
    toggleSelected: (state, action) => {
      const asin = action.payload;
      const idx = state.selected.indexOf(asin);
      if (idx >= 0) state.selected.splice(idx, 1);
      else state.selected.push(asin);
    },
    setSelected: (state, action) => {
      state.selected = action.payload;
    },
    clearSelected: (state) => {
      state.selected = [];
    },
    removeAsinsLocally: (state, action) => {
      const removed = new Set(action.payload);
      state.watchlist = state.watchlist.filter((it) => !removed.has(it.asin));
      state.selected = state.selected.filter((a) => !removed.has(a));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAsinWatchlist.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAsinWatchlist.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.status === "authorized") {
          state.watchlist = action.payload.data || [];
          state.totalItems = action.payload.total_count || 0;
        }
      })
      .addCase(fetchAsinWatchlist.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(addSingleAsin.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addSingleAsin.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.status === "authorized") {
          state.trackedAsins = action.payload.tracked_asins ?? state.trackedAsins;
          state.asinsLeft = action.payload.asins_left ?? state.asinsLeft;
        }
      })
      .addCase(addSingleAsin.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(deleteSingleAsin.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteSingleAsin.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.status === "authorized") {
          state.trackedAsins = action.payload.tracked_asins ?? state.trackedAsins;
          state.asinsLeft = action.payload.asins_left ?? state.asinsLeft;
        }
      })
      .addCase(deleteSingleAsin.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(deleteAsinsBatch.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteAsinsBatch.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.status === "authorized") {
          state.trackedAsins = action.payload.tracked_asins ?? state.trackedAsins;
          state.asinsLeft = action.payload.asins_left ?? state.asinsLeft;
          state.selected = [];
        }
      })
      .addCase(deleteAsinsBatch.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(exportAsins.pending, (state) => {
        state.status = "loading";
      })
      .addCase(exportAsins.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(exportAsins.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchPriceAlertInfo.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPriceAlertInfo.fulfilled, (state, action) => {
        if (action.payload?.status === "authorized") {
          state.asinTokens = action.payload.asin_tokens;
          state.asinsLeft = action.payload.asins_left;
          state.asinsLocked = action.payload.asins_locked;
          state.trackedAsins = action.payload.tracked_asins;
        }
      })
      .addCase(fetchPriceAlertInfo.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export const {
  setAuthStatus,
  setCurrentPage,
  setFilter,
  resetFilters,
  toggleSelected,
  setSelected,
  clearSelected,
  removeAsinsLocally,
} = asinWatchlistSlice.actions;
export default asinWatchlistSlice.reducer;
